import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SmartMenu from "@/components/SmartMenu";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { Plus, MapPin, Users, X, ArrowRight, Edit2, Trash2 } from "lucide-react";

export default function JobsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs");
      setJobs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const canManage = user?.role === "admin" || user?.role === "supervisor";
  const isAdmin = user?.role === "admin";

  const deleteJob = async (j) => {
    if (!window.confirm(`Delete "${j.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      toast.success("Job site deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Job Sites</div>
          <h1 className="font-heading text-4xl md:text-6xl">ACTIVE PROJECTS</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            Crews assigned to named job sites · <b className="text-foreground">{jobs.length} total</b>
          </p>
        </div>
        {canManage && (
          <button data-testid="new-job-btn" onClick={() => setShowNew(true)} className="btn-primary">
            <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> New Job Site
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="font-heading text-2xl">No job sites yet</div>
          <div className="text-muted-foreground text-sm mt-2">Create one to start assigning crews.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
          {jobs.map((j) => (
            <div key={j.id} role="link" tabIndex={0} data-testid={`job-card-${j.id}`}
              onClick={() => navigate(`/jobs/${j.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/jobs/${j.id}`); }}
              className="brutal-card p-5 cursor-pointer hover:-translate-y-0.5 hover:-translate-x-0.5 transition-transform group">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="font-heading text-2xl leading-tight">{j.name}</div>
                <div className="h-10 w-10 bg-safety-yellow text-black border-2 border-black flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <Users className="w-5 h-5" strokeWidth={2.5} />
                </div>
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-4">
                <MapPin className="w-3.5 h-3.5" /> {j.location || "No location set"}
              </div>
              <div className="flex items-center justify-between border-t-2 border-dashed border-foreground/30 dark:border-safety-yellow/40 pt-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Crew Size</div>
                  <div className="font-heading text-3xl">{j.employee_count}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-safety-yellow group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight className="w-5 h-5" strokeWidth={3} />
                  </div>
                  {canManage && (
                    <SmartMenu
                      testId={`job-menu-${j.id}`}
                      items={[
                        { label: "Edit Project", icon: Edit2, onClick: () => setEditing(j), testId: `job-edit-${j.id}` },
                        { label: "Delete Project", icon: Trash2, onClick: () => deleteJob(j), danger: true, testId: `job-delete-${j.id}`, hidden: !isAdmin },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <JobFormModal title="New Job Site" submitLabel="Create Job Site"
          onClose={() => setShowNew(false)}
          onSubmit={async (form) => {
            await api.post("/jobs", form);
            toast.success("Job site created");
            setShowNew(false); load();
          }} />
      )}

      {editing && (
        <JobFormModal title="Edit Project" submitLabel="Save Changes" initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (form) => {
            await api.patch(`/jobs/${editing.id}`, form);
            toast.success("Job site updated");
            setEditing(null); load();
          }} />
      )}
    </div>
  );
}

function JobFormModal({ title, submitLabel, initial, onClose, onSubmit }) {
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [form, setForm] = useState({
    name: initial?.name || "",
    location: initial?.location || "",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6">
      <div className="bg-background brutal-card w-full md:max-w-lg">
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4">
          <div className="font-heading text-2xl">{title}</div>
          <button onClick={onClose} data-testid="job-modal-close"
            className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Job Name</label>
            <input data-testid="job-name" required className="industrial-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Location</label>
            <input data-testid="job-location" className="industrial-input" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Start</label>
              <input data-testid="job-start" type="date" className="industrial-input" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">End</label>
              <input data-testid="job-end" type="date" className="industrial-input" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <button data-testid="job-save" disabled={saving} type="submit" className="btn-primary w-full">
            {saving ? "Saving..." : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
}
